/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {withTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import Container from '@material-ui/core/Container';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/router';
import {SnackbarProvider} from 'notistack';
import {useAuthLogin} from '@ion/applications/src/authentication/useAuthLogin';
import {BulkActionDialog} from '@ion/applications/src/bulk/BulkActionDialog';
import {useBulkActionDialog} from '@ion/applications/src/bulk/useBulkActionDialog';
import GlobalSearchEditor from '@ion/applications/src/globalsearch/GlobalSearchEditor';
import {toggleGlobalSearch} from '@ion/applications/src/globalsearch/state';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {useShowProfile} from '@ion/applications/src/hooks/useShowProfile';
import styles from '@ion/applications/src/layout/Layout.module.scss';
import LayoutContent, {LayoutContentProps} from '@ion/applications/src/layout/LayoutContent';
import {useLayoutStyle} from '@ion/applications/src/layout/useLayoutStyle';
import {ssoConfigPermission} from '@ion/applications/src/pages/admin/accounts/SSOEditorUtil';
import {accountsMapSelector} from '@ion/applications/src/pages/admin/groups/state-selectors';
import ChangePassword from '@ion/applications/src/pages/admin/users/ChangePassword';
import EditUserEditor from '@ion/applications/src/pages/admin/users/EditUserEditor';
import {useAuthenticatedUser} from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import {
  downloadJobsPendingJobsSelector,
  showPendingJobsSelector
} from '@ion/applications/src/pages/devices/selector';
import {JobStatusDrawer} from '@ion/applications/src/pages/jobstatus/JobStatusDrawer';
import {OfflineNotification} from '@ion/applications/src/prompts/OfflineNotification';
import {translate} from '@ion/core/src/locale/locale';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {AccountLink, Entity} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import {useExpandCollapse} from '@ion/dls/src/components/navigation/useExpandCollapse';
import {ErrorSnackbar} from '@ion/dls/src/components/snackbars/ErrorSnackbar';
import {SnackbarMessage} from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
import {dlsColor} from '@ion/dls/src/theme/theme';
import useAutojobsAttribute from '@ion/query/src/hooks/useAutojobsAttribute';
import {SessionExpired} from '@/components/notifications/SessionExpired';
import {ExportStatusPrompt} from '@/components/prompts/ExportStatusPrompt';
import {GlobalUserPrompt} from '@/components/prompts/GlobalUserPrompt';
import ResponsiveHeader from '@/header/ResponsiveHeader';
import SideNav from '@/nav/SideNav';
import {PB_STEPS} from '@/routes/admin/pegbehaviors/PegBehaviorsEditorUtils';
import ViewDownloadJobsStatus from '@/routes/provisioning/jobs/ViewDownloadJobsStatus';
import {getRoute, getRouteLabel, Route} from '@/routes/RouteDefinitions';
import Features, {ADMIN_PEG_BEHAVIORS, DASHBOARD_PAGE} from '@/security/Features';
import {getAdminPbPegLabels} from '@/store/actions/admin-pegbehaviors';
import {setGlobalGroup} from '@/store/actions/global-group-chooser';
import {refreshInitialization} from '@/store/actions/initialization';
import {login, loginSSO} from '@/store/actions/login';
import {isValidLoginSelector} from '@/store/selectors/layout';
import DeviceActionDialogs from '../components/dialogs/DeviceActionDialogs';
import FeatureFlags, {FeatureFlag} from '../security/FeatureFlags';
import {useHasPermission} from '../security/useHasPermission';
import useHasSSOCreatePermission from '../security/useHasSSOCreatePermission';
import DisplayContent from './DisplayContent';

const useStyles = makeStyles(theme => ({
  notificationRoot : {
    top : 150
  },
  mobileNotificationRoot : {
    top                 : 0,
    '& > *:first-child' : {
      paddingLeft  : 0,
      paddingRight : 0
    }
  }
}));

/**
 * Defines the top-level application Layout which includes the Side Navigation and
 * Header Component (Top Navigation). All Pages except the Login Page are wrapped
 * by the Layout Component via the Application component (_app.tsx).
 *
 * ```typescript
 *  const Page = ({tReady}: LocalizedPageProps) => (
 *    <>
 *      <PageTitle namespace={'admin-groups'} initialized={tReady} />
 *      {tReady ? <AdminGroups /> : <ContentLoader fullPage />}
 *    </>
 *  );
 *
 *  const AdminGroupsPage = withTranslation([
 *    'admin-groups'
 *  ])(Page) as PageLayoutConsumer;
 *
 *  // implement getLayoutProps to override the default grid prop value ...
 *  AdminGroupsPage.getLayoutProps = () => ({grid: true});
 *
 *  export default AdminGroupsPage;
 *
 * ```
 */
const Layout: FC = ({children, grid = true, backgroundColor = dlsColor('grey6')}: LayoutContentProps) => {
  const theme                             = useTheme();
  const classes                           = useStyles();
  const route                             = useRouter();
  const dispatch                          = useDispatch();
  const isPhone                           = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showJob, setShowJob]             = useState<boolean>(false);
  const [showError, setShowError]         = useState<boolean>(false);
  const currentGlobalGroupId              = useCurrentGlobalGroupId();
  const {id: loggedInUserId}              = useAuthenticatedUser();
  const {show: showBulkDialog}            = useBulkActionDialog();
  const {collapsed, onHandleCollapse}     = useExpandCollapse();
  const isDesktop                         = useIsDesktop();
  const showProfile                       = useShowProfile();
  const globalAccIdRef                    = useRef(null);
  const isPegBehaviorPage                 = route?.pathname === getRoute(Route?.ADMIN_PEG_BEHAVIORS);
  const isJobSearchEnginePage             = route?.pathname === getRoute(Route?.PROVISIONING_JOBS_SEARCH_ENGINE);
  const hasSsoConfigPermission            = useHasSSOCreatePermission();
  const accountsMap                       = useSelector(accountsMapSelector);

  const groupsPermissions = {
    hasUpdatePermission : useHasPermission(Entity.group, Operation.Update) || false,
    hasReadPermission   : useHasPermission(Entity.group, Operation.Read) || false,
    hasDeletePermission : useHasPermission(Entity.group, Operation.Delete) || false
  };

  const usersPermissions = {
    hasAddPermission    : useHasPermission(Entity.user, Operation.Create) || false,
    hasReadPermission   : useHasPermission(Entity.user, Operation.Read) || false,
    hasUpdatePermission : useHasPermission(Entity.user, Operation.Update) || false,
    hasDeletePermission : useHasPermission(Entity.user, Operation.Delete) || false
  };

  const assetsPermissions = {
    hasAddPermission    : useHasPermission(Entity.asset, Operation.Create) || false,
    hasReadPermission   : useHasPermission(Entity.asset, Operation.Read) || false,
    hasUpdatePermission : useHasPermission(Entity.asset, Operation.Update) || false,
    hasDeletePermission : useHasPermission(Entity.asset, Operation.Delete) || false
  };

  const accountsPermissions = {
    hasAddPermission       : useHasPermission(Entity.account, Operation.Create) || false,
    hasSsoConfigPermission : useHasPermission(ssoConfigPermission, Operation.Create) || false,
    hasSpEditPermission    : useHasPermission(Entity.serviceprofile, Operation.Update) || false,
    hasReadPermission      : useHasPermission(Entity.account, Operation.Read) || false,
    hasUpdatePermission    : useHasPermission(Entity.account, Operation.Update) || false,
    hasDeletePermission    : useHasPermission(Entity.account, Operation.Delete) || false
  };

  const customVisionPermissions = {
    hasUpdatePermission : Features.hasPermission(Entity.customvisionsettings, Operation.Update) || false
  };

  const devicesPermissions = {
    hasAddPermission    : useHasPermission(Entity.device, Operation.Create) || false,
    hasReadPermission   : useHasPermission(Entity.device, Operation.Read) || false,
    hasUpdatePermission : useHasPermission(Entity.device, Operation.Update) || false,
    hasDeletePermission : useHasPermission(Entity.device, Operation.Delete) || false
  };

  const jobsPermissions = {
    hasAddPermission    : useHasPermission(Entity.job, Operation.Create) || false,
    hasReadPermission   : useHasPermission(Entity.job, Operation.Read) || false,
    hasUpdatePermission : useHasPermission(Entity.job, Operation.Update) || false,
    hasDeletePermission : useHasPermission(Entity.job, Operation.Delete) || false
  };

  const hasCreateJob = useHasPermission(Entity.job, Operation.Create) || false;

  const {
    authenticated: isAuthenticated,
    isInitialized
  } = useSelector(isValidLoginSelector);

  const showPendingJobs = useSelector(showPendingJobsSelector);
  const pendingJobs     = useSelector(downloadJobsPendingJobsSelector);

  useAutojobsAttribute({
    accountId : currentGlobalGroupId,
    enabled   : true
  });
  
  /**
 * @deprecated
 *
 * This permission check will need to be updated to query for the feature
 * and scope (module); e.g. Features.hasFeatureScope(VEHICLE_PROFILE, READ);
 */
  const hasVehicleProfile      = useHasPermission(Entity.vin, Operation.Update);
  const hasDevicePassword      = useHasPermission(Entity.devicepassword, Operation.Create);
  const hasPegBehaviorsFeature = Features.hasFeature(ADMIN_PEG_BEHAVIORS);
  const pageInitialized        = isAuthenticated && isInitialized;
  const isVbuFwEnabled         = FeatureFlags.isEnabled(FeatureFlag.VBU_FW);

  // when a page is initialized, if the user has not been authenticated, we redirect
  // to the login page ...
  useAuthLogin({
    isAuthenticated,
    onLogin   : login,
    onLoginV2 : loginSSO
  });

  // Initiates an account change to the given account.
  const onSelectAccount = useCallback(function(newAccount: AccountLink | null) {
    if (!newAccount) {
      setShowError(true);
    } else {
      dispatch(setGlobalGroup(newAccount) as any);
    }
  }, []);

  const onDismissErrorSnackbar = useCallback(() => {
    setShowError(false);
  }, []);

  const onCloseDialog = useCallback(function() {
    dispatch(toggleGlobalSearch(false));
  }, []);

  // Fetch All Peg Labels data of PEG Events/Accumulators type...
  const fetchAllPegLabels = async function(accountId: number) {
    try {
      const result = await Promise.all([
        dispatch(getAdminPbPegLabels({
          accountId,
          step : PB_STEPS.EVENT_CODE
        }) as any),
        dispatch(getAdminPbPegLabels({
          accountId,
          step : PB_STEPS.ACCUMULATORS
        }) as any)
      ]);
    } catch (error) {
      error(translate('admin-pegbehaviors:fetchPegLabels.error', {accountId: currentGlobalGroupId}));
    }
  };

  /**
   * Invoke fetchAllPegLabels method only once at the time of Login. Check if user has
   * Peg Behaviors permission, page is initialized and global account Id is present.
   */
  useEffect(() => {
    if (hasPegBehaviorsFeature && pageInitialized && currentGlobalGroupId) {
      globalAccIdRef.current = currentGlobalGroupId;
      fetchAllPegLabels(currentGlobalGroupId);
    }
  }, [hasPegBehaviorsFeature, pageInitialized]);

  /**
   * Re-invoke the fetchAllPegLabels method in the PEG behavior page only when there is
   * a change in the global account ID.
   */
  useEffect(() => {
    if (hasPegBehaviorsFeature && pageInitialized && isPegBehaviorPage && currentGlobalGroupId) {
      if (globalAccIdRef.current !== currentGlobalGroupId) {
        fetchAllPegLabels(currentGlobalGroupId);
        globalAccIdRef.current = currentGlobalGroupId;
      }
    }
  }, [hasPegBehaviorsFeature, pageInitialized, isPegBehaviorPage, currentGlobalGroupId]);

  const selectedJobData = useCallback((jobId: number) => {
    setSelectedJobId(jobId);
    setShowJob(true);
  }, [selectedJobId]);

  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: openBulkSnackbar,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  const onBulkComplete = useCallback(function(message: string): void {
    openBulkSnackbar(message);
  }, []);

  const layoutStyle = useLayoutStyle({
    useExpandCollapse : isDesktop,
    collapsed         : collapsed,
    backgroundColor,
    showProfile
  });

  return (
    <Container maxWidth='xs'>
      {(selectedJobId || showPendingJobs) && (
        <ViewDownloadJobsStatus selectedJobId={selectedJobId || pendingJobs?.jobId}
          showJob={setShowJob}
          setSelectedJobId={setSelectedJobId}
          open={showJob || showPendingJobs}
          jobsPermissions={jobsPermissions} />
      )}
      {showBulkDialog && (
        <BulkActionDialog onComplete={onBulkComplete}
          Features={Features}
          accountId={currentGlobalGroupId}
          userId={loggedInUserId}
          hasCreateJob={hasCreateJob}
          hasUpdateAsset={assetsPermissions?.hasUpdatePermission}
          hasCreateDevice={devicesPermissions?.hasAddPermission}
          hasVehicleProfile={hasVehicleProfile}
          hasDevicePassword={hasDevicePassword}
          jobSearchEngineEnabled
          omitExtensions
          isBulkAction />
      )}
      <SnackbarMessage show={showBulkSnackbar}
        message={bulkSnackbarMessage}
        onDismiss={dismissBulkSnackbar} />
      <SnackbarProvider maxSnack={isPhone ? 1 : 3}
        anchorOrigin={{
          vertical   : 'top',
          horizontal : 'right'
        }}
        classes={{
          containerRoot : isPhone ? classes.mobileNotificationRoot : classes.notificationRoot
        }}>
        <SessionExpired />
        <ResponsiveHeader />
        <SideNav {...{onToggleCollapse: onHandleCollapse}} />
        <div className={styles[isPhone ? 'phone' : 'desktop']} style={layoutStyle}>
          <LayoutContent initialized={pageInitialized}
            useExpandedWidth={isJobSearchEnginePage}
            grid={grid}>
            <DisplayContent>{children}</DisplayContent>
          </LayoutContent>
        </div>
        <OfflineNotification />
        {showError && (
          <ErrorSnackbar show={showError}
            message={translate('common:groupChooser.changeError')}
            onDismiss={onDismissErrorSnackbar} />
        )}
      </SnackbarProvider>
      <GlobalUserPrompt />
      <ExportStatusPrompt />
      <DeviceActionDialogs selectedJobId={selectedJobData}
        devicesPermissions={devicesPermissions}
        Features={Features}
        hasCreateJob={hasCreateJob}
        isToggleSwitchGrid={true}
        isShowColumnChooserIcon={false}
        isShowRefreshButton={false}
        isVbuFwEnabled={isVbuFwEnabled}
        isDeviceNotes
        assetsPermissions={assetsPermissions}
        jobSearchEngineEnabled
        isCtcAdmin
        includeGroupProperties={false}
        showTreeRefresh />
      <EditUserEditor isCtcAdmin
        context={UserContext.ctcAdmin}
        defaultPageId={DASHBOARD_PAGE}
        includeApplications
        includeAddresses
        includeContacts
        includeGroupProperties={false}
        usersPermissions={usersPermissions}
        getRouteLabel={getRouteLabel}/>
      {usersPermissions.hasUpdatePermission && (
        <ChangePassword isPhrase />
      )}
      <GlobalSearchEditor refreshInitialization={refreshInitialization}
        hasSsoConfigPermission={hasSsoConfigPermission}
        selectedJobId={selectedJobData}
        onSelectAccount={onSelectAccount}
        getRouteLabel={getRouteLabel}
        onCloseDialog={onCloseDialog}
        Features={Features}
        hasCreateJob={hasCreateJob}
        devicesPermissions={devicesPermissions}
        defaultPageId={DASHBOARD_PAGE}
        includeGroupProperties={false}
        assetsPermissions={assetsPermissions}
        usersPermissions={usersPermissions}
        groupsPermissions={groupsPermissions}
        accountsMap={accountsMap}
        hasInheritableGroups
        accountsPermissions={accountsPermissions}
        customVisionPermissions={customVisionPermissions}
        includeDeviceGridCheckboxes
        includeAssetGridCheckboxes
        isGlobalSearchMaxLength
        omitHeader
        showTimeoutError
        includeGroups
        isCtcAdmin
        usePreferedPageSize
        isInfiniteScroll
        showTreeRefresh />
      <JobStatusDrawer hasEmailExport
        selectedJobId={selectedJobData} />
    </Container>
  );
};
export default withTranslation([
  'common',
  'nav',
  'grids',
  'notifications',
  'timezones',
  'countries',
  'contacts',
  'states',
  'admin-groups',
  'admin-users',
  'admin-assets',
  'admin-devices',
  'maps',
  'groups',
  'jobstatus',
  'downloadjobs',
  'jobsearchengine'
])(React.memo(Layout));
